import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import CImage from "@/bit/components/components.c-image";
import TextInfo from "@/bit/components/components.text";
import getColor from "@gcloud-npm/utils.enums.colors";
import DetailCard from "@/bit/components/components.cms-block.cms-detail-card";
import RuleEvaluator from "@gcloud-npm/utils.rule-evaluator";
import SecureLocalStorage from "@gcloud-npm/utils.secure.local-storage";
import Carousel from "@/bit/components/components.carousel";
import emptyCard from "./emptyCard.json";
import getUrlParams from "@gcloud-npm/utils.common.get-url-params";

const IconGlyph = dynamic(() => import("@/bit/components/components.icon-glyph"));
const CmsAddElement = dynamic(() =>
  import("@/bit/components/components.cms-block.cms-add-element")
);
const ID_TEMPLATE_ORION = "253888";

const checkUserPQ = (ts, rules) => {
  let res = false;
  let ruleEvaluator = null;
  try {
    if (Object.keys(rules).length > 0) {
      ruleEvaluator = new RuleEvaluator(rules);
      res = ruleEvaluator.test(ts);
    }
  } catch (error) {
    console.log("Error checkUserPQ: ", error);
  }
  return res;
};

function isEmptyObject(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0
  );
}

const filterListByJsonRules = (list, ts) => {
  return list.filter((item) => {
    const jsonRules = item["cms-hiring-details-1"]?.["cms-json-rules-1"];
    const isEmptyOrNull = !jsonRules || isEmptyObject(jsonRules);

    return isEmptyOrNull || checkUserPQ(ts, jsonRules);
  });
};

/**
 * Componente que permite crear un carrousel de DetailCard
 */
function DetailCardList(props) {
  const {
    cmsHandler,
    blockName,
    cms,
    alias,
    carrusel,
    contentLoaded = true,
  } = props;
  const color = props.color || "white";
  const backgroundColor = getColor(color);
  const titleProps = props["cms-atom-text-1"];
  const imageProps = props["cms-atom-image-1"];
  const cardsLayout = props["cards-layout"];
  const cardList = props["cms-ml-detail-card-1"];
  const className = "cms-detail-card-list";

  const [filteredList, setFilteredList] = useState(cardList);

  if (
    !cardList ||
    !Array.isArray(cardList) ||
    (cardList.length === 0 && !cms)
  ) {
    return null;
  }

  // Filtramos por parametro y por json rules
  let listToRender = cmsHandler ? cardList : filteredList;

  // Icono Añadir elemento
  const addElementCmp = (index = "start") =>
    cmsHandler && (
      <CmsAddElement
        addProps={{
          index,
          idTemplateOrion: ID_TEMPLATE_ORION,
          itemIdDotNotation: `${blockName}.cms-ml-detail-card-1`,
          alias,
        }}
        cmsHandler={cmsHandler}
      />
    );

  useEffect(() => {
    // Extraemos parametros de url
    const urlParams = getUrlParams({
      groupByObject: false,
      allowedParams: false,
      excludeHash: true,
      currentUrl: window.location.href,
    });

    // Extraemos secure y concatenamos con parametros
    const secureStorage = new SecureLocalStorage();
    const ts = { ...secureStorage.getItem("ts"), urlParams };

    // Actualizamos lista con filtros de json rules
    setFilteredList(filterListByJsonRules(cardList, ts));
  }, [cardList]);

  return (
    <section id={blockName || ""}>
      <div
        className="cms-detail-card-list--title"
        style={{ backgroundColor: backgroundColor }}
      >
        <div className="container">
          <div className="title-gap">
            <TextInfo {...titleProps} cmsHandler={cmsHandler} />
          </div>
        </div>
      </div>
      <div className={className} style={{ backgroundColor: backgroundColor }}>
        {/* Imagen de fondo */}
        <CImage {...imageProps} fill cmsHandler={cmsHandler} />
        {cmsHandler && (
          <span
            className={"element-editable block edit-component"}
            onClick={() =>
              cmsHandler({
                ...props,
                name: "cms-block.cms-detail-card-list",
              })
            }
          >
            <IconGlyph id="lapiz-m" height={28} color="white" />
          </span>
        )}
        <div className="container">
          <div className="content-gap">
            {/* Card placeholder cuando la lista es vacia */}
            {cms && listToRender.length === 0 ? (
              <Carousel maxPointNum={0} breakpoint={768} slideSeparation={16}>
                {addElementCmp("end")}
                <DetailCard detailCardProps={emptyCard} />
              </Carousel>
            ) : (
              <>
                {carrusel === true || carrusel === "true" ? (
                  <Carousel
                    maxPointNum={0}
                    breakpoint={768}
                    slideSeparation={16}
                  >
                    {[
                      addElementCmp("start"),
                      ...listToRender.map((item, index) => (
                        <DetailCard
                          detailCardProps={{
                            ...item,
                            contentLoaded,
                            layout: cardsLayout,
                          }}
                          cmsHandler={cmsHandler}
                          index={index}
                          alias={alias}
                          blockName={blockName}
                          key={`detail-card-${index}`}
                          frontalListToDelete={listToRender}
                        />
                      )),
                      addElementCmp("end"),
                    ].filter(Boolean)}
                  </Carousel>
                ) : (
                  <div className={`${className}--grid`}>
                    {[
                      addElementCmp("start"),
                      listToRender.map((item, index) => (
                        <DetailCard
                          detailCardProps={{
                            ...item,
                            contentLoaded,
                            layout: cardsLayout,
                          }}
                          index={index}
                          alias={alias}
                          blockName={blockName}
                          cmsHandler={cmsHandler}
                          key={`detail-card-${index}`}
                          frontalListToDelete={listToRender}
                        />
                      )),
                      addElementCmp("end"),
                    ].filter(Boolean)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`.cms-detail-card-list--title {
  background-color: white;
  padding-top: 1rem 0;
  position: relative;
  .title-gap {
    padding-bottom: 1rem;
  }
}

.cms-detail-card-list {
  background-color: white;
  padding-bottom: 1rem;
  position: relative;

  hr.separator {
    color: rgb(221, 221, 221);
    margin-top: 0.5rem;
    margin-bottom: 2rem;
  }

  &--grid {
    position: relative;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
    &::-webkit-scrollbar {
      display: none;
    }
  }
  @media screen and (max-width: 768px) {   
    &--grid {
      display: flex;
      gap: 4px;
      overflow-x: auto;
      overflow-y: hidden;
      height: 100%;
      :global(.DetailCard) {
        height: auto !important;
      }
    }
  }
}
`}</style>
    </section>
  );
}

DetailCardList.propTypes = {
  /**
   * {
   *  "info": "Permite heredar cards implementadas en otras fichas, hay que indicarlo empleando la notacion: [alias]::[objectDotPath]::[start|end]",
   *  "kind": "both",
   *  "beautifulName": "Heredar de:"
   * }
   */
  cmsgetfrom: PropTypes.string,
  /**
   * {
   *  "info": "Define si las cards se muestran en carrusel o en grid",
   *  "kind": "both",
   *  "beautifulName": "Carrusel"
   * }
   */
  carrusel: PropTypes.string,
  /**
   * {
   *  "info": "Define el layout de las cards, en caso de querer otro estilo",
   *  "kind": "both",
   *  "beautifulName": "Layout (ancho) de las cards",
   *  "values": "./enums.js:CARDS_LAYOUT"
   * }
   */
  "cards-layout": PropTypes.string,
  /**
   * {
   *  "info": "Color de fondo",
   *  "kind": "both",
   *  "beautifulName": "Color de fondo"
   * }
   */
  color: PropTypes.string,
};

DetailCardList.defaultProps = {
  carrusel: "true",
  color: "white",
  "cards-layout": "default",
};

export default DetailCardList;
